import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { InitiativeStatus } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import {
  EKeyInitiativeGoalMode,
  getPersistedKeyInitiativeGoalMode,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import {
  normalizeKeyInitiativeStatus,
  STATUS_OPTIONS,
} from 'partner360/entities/keyInitiatives/model/status'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { DashboardChartMeasure, DashboardGroupBy } from '../../../model/types'
import { IListFinancialTotals, TGroupedInitiativeRow, TListGroupBy, TListMeasure } from '../models'

import { getFinGoalValue } from './getFinGoal'
import {
  EMPTY_LIST_VALUE,
  getListBusinessPriorities,
  getListInitiativeKey,
  getListStrategicPath,
  splitListValues,
} from './listDictionaries'
import { getListQuarters, LIST_QUARTERS } from './listPeriod'

// Risk first; preserve the shared options' order for all other statuses.
export const LIST_STATUS_ORDER: string[] = [...STATUS_OPTIONS]
  .sort(
    (left, right) =>
      Number(right.key === InitiativeStatus.AtRisk) - Number(left.key === InitiativeStatus.AtRisk)
  )
  .map(({ key }) => key)

export const getListMeasureValue = (
  row: TGroupedInitiativeRow,
  measure: TListMeasure
): number | null => {
  if (measure === DashboardChartMeasure.Count) return 1
  if (measure === 'no-sales-target')
    return row.finGoalType === EKeyInitiativeGoalMode.NoSalesTarget ? 1 : 0
  const targetMode =
    measure === DashboardChartMeasure.Incremental
      ? EKeyInitiativeGoalMode.Incremental
      : EKeyInitiativeGoalMode.RunRate
  return row.finGoalType === targetMode ? (row.finGoal ?? null) : 0
}

export const getListMeasureTotal = (
  rows: TGroupedInitiativeRow[],
  measure: TListMeasure
): number | null => {
  const values = rows
    .map((row) => getListMeasureValue(row, measure))
    .filter((value): value is number => value !== null)
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null
}

export const getListFinancialTotals = (rows: TGroupedInitiativeRow[]): IListFinancialTotals => ({
  runRate: getListMeasureTotal(rows, DashboardChartMeasure.Goal),
  incremental: getListMeasureTotal(rows, DashboardChartMeasure.Incremental),
})

export const getInitiativeListRows = (
  data: TKeyInitiative[],
  keyObjectives: IKeyObjective[],
  priorities: IKIBusinessPriority[],
  actualsType: ActualsValueTypes,
  activeCurrency: string
): TGroupedInitiativeRow[] =>
  data.map((initiative) => {
    const { path } = getListStrategicPath(initiative, keyObjectives)
    const finGoalType = getPersistedKeyInitiativeGoalMode(initiative)
    return {
      ...initiative,
      Status: normalizeKeyInitiativeStatus(initiative.Status),
      source: initiative,
      rowKey: getListInitiativeKey(initiative),
      strategicPath: path,
      developmentBreadcrumb: path.join(' → '),
      businessPriorityNames: getListBusinessPriorities(
        initiative.BusinessPriorities,
        priorities
      ).map((priority) => priority.name),
      finGoalType,
      finGoal: getFinGoalValue(initiative, finGoalType, actualsType, activeCurrency),
    }
  })

/** Presentation fork of the existing table: retain multi-priority membership and quarter allocation. */
export const getGroupedInitiativeRows = (
  data: TKeyInitiative[],
  groupBy: TListGroupBy,
  keyObjectives: IKeyObjective[],
  priorities: IKIBusinessPriority[],
  actualsType: ActualsValueTypes,
  activeCurrency: string,
  measure: TListMeasure = DashboardChartMeasure.Goal,
  translate: (value: string) => string = (value) => value
): TGroupedInitiativeRow[] => {
  const rows = getInitiativeListRows(data, keyObjectives, priorities, actualsType, activeCurrency)
  if (groupBy === 'none') return rows
  const groups = new Map<string, TGroupedInitiativeRow[]>()
  rows.forEach((row) => {
    const groupValues = (() => {
      switch (groupBy) {
        case DashboardGroupBy.StrategicInitiative:
          return [row.strategicPath?.[0] ?? EMPTY_LIST_VALUE]
        case DashboardGroupBy.BusinessPriority:
          return row.businessPriorityNames ?? []
        case DashboardGroupBy.BusinessUnit:
          return splitListValues(row.BusinessUnit)
        case DashboardGroupBy.Period:
          return getListQuarters(row.Period)
        case DashboardGroupBy.Status:
          return row.Status?.trim() ? [row.Status.trim()] : []
      }
    })()
    const keys = groupValues.length ? [...new Set(groupValues)] : [EMPTY_LIST_VALUE]
    keys.forEach((groupKey) => {
      const financialDivider = groupBy === DashboardGroupBy.Period ? keys.length : 1
      const groupRows = groups.get(groupKey) ?? []
      groupRows.push({
        ...row,
        groupKey,
        rowKey: JSON.stringify([groupBy, groupKey, row.rowKey]),
        finGoal: row.finGoal == null ? null : row.finGoal / financialDivider,
      })
      groups.set(groupKey, groupRows)
    })
  })
  const summaries = [...groups].map(([groupKey, items]) => ({
    groupKey,
    items,
    total: getListMeasureTotal(items, measure),
  }))
  const orderIndex = (values: readonly string[], key: string) => {
    const index = values.indexOf(key)
    return index === -1 ? values.length : index
  }
  summaries.sort((left, right) => {
    if (left.groupKey === EMPTY_LIST_VALUE) return right.groupKey === EMPTY_LIST_VALUE ? 0 : 1
    if (right.groupKey === EMPTY_LIST_VALUE) return -1
    if (groupBy === DashboardGroupBy.Period)
      return orderIndex(LIST_QUARTERS, left.groupKey) - orderIndex(LIST_QUARTERS, right.groupKey)
    if (groupBy === DashboardGroupBy.Status)
      return (
        orderIndex(LIST_STATUS_ORDER, left.groupKey) -
          orderIndex(LIST_STATUS_ORDER, right.groupKey) ||
        translate(left.groupKey).localeCompare(translate(right.groupKey))
      )
    return (
      (right.total ?? 0) - (left.total ?? 0) ||
      translate(left.groupKey).localeCompare(translate(right.groupKey))
    )
  })
  return summaries.flatMap(({ groupKey, items, total }) => {
    const statuses = [...new Set(items.map((item) => item.Status?.trim() || EMPTY_LIST_VALUE))]
    const groupRow: TGroupedInitiativeRow = {
      isGroupRow: true,
      rowKey: JSON.stringify(['group', groupBy, groupKey]),
      groupKey,
      groupItemsCount: items.length,
      groupMeasureValue: total,
      groupFinancialTotals: getListFinancialTotals(items),
      groupStatusCounts: statuses
        .sort(
          (left, right) =>
            orderIndex(LIST_STATUS_ORDER, left) - orderIndex(LIST_STATUS_ORDER, right)
        )
        .map((status) => ({
          status,
          count: items.filter((item) => (item.Status?.trim() || EMPTY_LIST_VALUE) === status)
            .length,
        })),
    }
    return [groupRow, ...items]
  })
}
